<template>
  <div class="w-full pb-4 rounded">
    <div class="grid grid-cols-12 gap-4">
      <!-- Row 1 : Filters -->

      <!-- subgroup -->
      <div class="col-span-12 md:col-span-3">
        <label class="block text-sm font-medium text-slate-700 mb-1">Sub Group</label>
        <app-dropdown
v-model="filters.selectedSubGroup" :disabled="!store.isOnline"
          size="small" :options="subGroupOptions" label-key="name" value-key="value" searchable
          clearable @update:model-value="store.loadItems(true)">
          All Sub Groups
        </app-dropdown>
      </div>

      <div class="col-span-12 md:col-span-3">
        <label class="block text-sm font-medium text-slate-700 mb-1">Area</label>
        <app-dropdown
v-model="filters.selectedArea" :disabled="!store.isOnline"
          size="small" :options="areaOptions" label-key="name" value-key="value" clearable searchable
          @update:model-value="store.loadItems(true)">
          All Areas
        </app-dropdown>
      </div>

      <div class="col-span-12 md:col-span-3">
        <label class="block text-sm font-medium text-slate-700 mb-1">P Group</label>
        <app-dropdown
v-model="filters.selectedPGroup" :disabled="!store.isOnline"
          size="small" :options="pGroupOptions" label-key="name" value-key="value" searchable
          clearable @update:model-value="store.loadItems(true)">
          All P Groups
        </app-dropdown>
      </div>

      <div class="col-span-12 md:col-span-3">
        <label class="block text-sm font-medium text-slate-700 mb-1">Status</label>
        <app-dropdown
v-model="filters.selectedStatus" :disabled="!store.isOnline"
          size="small" :options="statusOptions" label-key="name" value-key="value" searchable
          clearable @update:model-value="store.loadItems(true)">
          All Statuses
        </app-dropdown>
      </div>

      <!-- Row 2: Search Input -->
      <div class="col-span-12 md:col-span-6">
        <div class="relative">
          <span class="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
            <svg
xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
              stroke="currentColor" stroke-width="2">
              <path
stroke-linecap="round" stroke-linejoin="round"
                d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15z" />
            </svg>
          </span>

          <input
v-model="filters.search" type="text" placeholder="Search..." :disabled="!store.isOnline"
            class="h-9 pl-10 pr-20 w-full px-2 border border-slate-400 rounded bg-white text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
            @keydown.enter="performSearch" />
          <button
:disabled="!store.isOnline" class="absolute inset-y-0 right-0 px-3 bg-mabey-950 hover:bg-mabey-900 text-white text-sm rounded-r transition disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"
            @click="performSearch">
            Search
          </button>
        </div>
      </div>

      <!-- Row 3: Buttons -->
      <div class="col-span-12 flex items-center justify-between gap-4">
        <div class="col-span-12 flex items-center justify-between gap-4">
          <button
:disabled="!store.isOnline" class="px-4 py-2 mt-3 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"
            @click="showAddItemForm = true">
            Add New Stock Item
          </button>
          <button
:disabled="!store.isOnline" class="px-4 py-2 mt-3 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"
            @click="showChangeAreaForm = true">
            Change Area
          </button>
        </div>


        <div class="flex items-center gap-4">
          <button
:disabled="!store.isOnline" class="px-3 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-md transition border border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
            @click="reset">
            Reset All Filters
          </button>

          <button
:disabled="!store.isOnline" class="px-4 py-2 bg-mabey-950 hover:bg-mabey-900 text-white text-sm rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"
            @click="sendToffice">
            Send to Office
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Add Item Form Slide-out Panel -->
  <AddItemForm :open="showAddItemForm" @close="showAddItemForm = false" />
  <ChangeAreaForm :open="showChangeAreaForm" @close="showChangeAreaForm = false" />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import useStockTake from '@/stores/stocktakestore'
import { storeToRefs } from 'pinia'
import { statusMap } from '@/utils/stocktakeapp'
import useToasterStore from '@/stores/toaster'
import type { StockItem } from '@/data/schema/stocktake'
import AddItemForm from './AddItemForm.vue'
import StockTakeService from '@/services/stocktake'
import ChangeAreaForm from './ChangeAreaForm.vue'


const store = useStockTake()
const toaster = useToasterStore()
const showAddItemForm = ref(false)
const showChangeAreaForm = ref(false)

const {
  selectedDepot,
  subGroups,
  pGroups,
  statuses,
  areas,
  filters,

} = storeToRefs(store)

// Computed properties to avoid re-mapping arrays on every render
const subGroupOptions = computed(() =>
  subGroups.value.map(s => ({ name: s, value: s }))
)

const areaOptions = computed(() =>
  areas.value.map(a => ({ name: a, value: a }))
)

const pGroupOptions = computed(() =>
  pGroups.value.map(p => ({ name: p, value: p }))
)

const statusOptions = computed(() =>
  statuses.value.map(s => ({ name: statusMap[s], value: s }))
)


const performSearch = () => {
  store.loadItems(true)
}

const sendToffice = async () => {
  store.isloading = true

  // Get items with status 1 (counted) and extract just their IDs
  const countedItemIds = store.items
    .filter((item: Partial<StockItem>) => Number(item.Status) === 1)
    .map((item: Partial<StockItem>) => Number(item.id))


  console.log(!!store.filters.selectedStatus)

  if (countedItemIds.length === 0 && !store.filters.selectedStatus) {
    toaster.error({
      text: 'Nothing to send (no counted items found).',
    })
    store.isloading = false
    return
  }

  try {
    toaster.working({ text: 'Sending counted items to office...' })

    const api = new StockTakeService()

    await api.exportStockItems(selectedDepot.value.prefix)

    toaster.clear()
    toaster.success({ text: `Successfully sent ${countedItemIds.length} items to office!` })

    // Refresh the list to show updated statuses
    await store.loadItems(true)

  } catch (err: any) {
    toaster.clear()

    const message = err?.response?.data?.message || err?.message || 'Failed to send items to office'
    toaster.error({ text: message })
  } finally {
    store.isloading = false
  }
}

function reset() {
  store.resetFilters()

  store.loadItems(true)
}
</script>
